import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StageBadge } from "@/pages/farm/shared";
import ReportProblemModal from "@/components/ReportProblemModal";
import { Printer, AlertTriangle } from "lucide-react";

export default function OrderDrawer({ orderId, onClose, canFulfill, onChanged }) {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const [escalateOpen, setEscalateOpen] = useState(false);

  const load = () => api.get(`/farm/orders/${orderId}`).then(r => {
    setOrder(r.data);
    setTracking(r.data.vendor_status?.tracking_url || "");
    setCarrier(r.data.vendor_status?.carrier || "");
  }).catch(e => setError(e.response?.data?.detail || "Load error"));

  useEffect(() => { if (orderId) load(); /* eslint-disable-next-line */ }, [orderId]);

  const update = async (status) => {
    setBusy(true); setError("");
    try {
      const payload = { status };
      if (tracking) payload.tracking_url = tracking;
      if (carrier) payload.carrier = carrier;
      await api.post(`/farm/orders/${orderId}/status`, payload);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const printSlip = () => {
    const w = window.open("", "_blank", "width=720,height=900");
    const items = order.items.map(i => `<tr><td>${i.name}</td><td style="text-align:right">${i.qty}</td><td style="text-align:right">$${(i.price*i.qty).toFixed(2)}</td></tr>`).join("");
    const addr = order.shipping_address || {};
    w.document.write(`<!DOCTYPE html><html><head><title>Packing Slip · ${order.id}</title>
      <style>body{font-family:Georgia,serif;padding:48px;color:#1a1a1a}h1{font-size:32px;margin:0}.muted{color:#888}table{width:100%;border-collapse:collapse;margin-top:24px}th,td{padding:10px 6px;border-bottom:1px solid #e5e5e5;text-align:left}.box{border:1px solid #ddd;padding:18px;margin-top:24px;border-radius:8px}</style>
      </head><body>
      <h1>The Farm To You</h1>
      <p class="muted">Packing slip · order #${order.id.slice(0,12)}</p>
      <div class="box"><strong>Ship to:</strong><br/>${order.customer?.name || ""}<br/>${addr.line1 || ""}<br/>${addr.city || ""}, ${addr.state || ""} ${addr.postal_code || ""}<br/>${addr.country || ""}</div>
      <table><thead><tr><th>Item</th><th style="text-align:right">Qty</th><th style="text-align:right">Subtotal</th></tr></thead><tbody>${items}</tbody></table>
      <p class="muted" style="margin-top:24px">Generated ${new Date().toLocaleString()}</p>
      <script>window.print()</script>
      </body></html>`);
    w.document.close();
  };

  if (!orderId) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="order-drawer">
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
          <div>
            <p className="overline">Order detail</p>
            <h2 className="font-display text-2xl mt-1" data-testid="drawer-order-id">#{orderId.slice(0,12)}</h2>
          </div>
          <button onClick={onClose} className="text-sm underline" data-testid="drawer-close">Close</button>
        </div>
        {!order && !error && <div className="p-10 text-[var(--ff-text-muted)]">Loading…</div>}
        {error && <div className="m-6 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">{error}</div>}
        {order && (
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              <StageBadge stage={order.vendor_status?.status || "paid"}/>
              <span className="text-xs text-[var(--ff-text-muted)]">Placed {order.created_at?.slice(0,10)}</span>
              <button onClick={() => setEscalateOpen(true)} className="ml-auto btn-secondary text-sm text-rose-700" data-testid="escalate-btn">
                <AlertTriangle size={14}/> Escalate to admin
              </button>
              <button onClick={printSlip} className="btn-secondary text-sm" data-testid="print-slip">
                <Printer size={14}/> Print packing slip
              </button>
            </div>
            <div className="card-flat p-4">
              <p className="overline">Items</p>
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between py-2 border-b last:border-b-0 text-sm" data-testid={`drawer-item-${i}`}>
                  <span>{it.name} <span className="text-[var(--ff-text-muted)]">× {it.qty}</span></span>
                  <span>${(it.price * it.qty).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 font-semibold text-sm">
                <span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span>
              </div>
            </div>
            <div className="card-flat p-4">
              <p className="overline">Ship to</p>
              <p className="mt-2 text-sm">{order.customer?.name || order.shipping_name || "—"}</p>
              {order.shipping_address ? (
                <p className="text-sm text-[var(--ff-text-secondary)] mt-1" data-testid="drawer-address">
                  {order.shipping_address.line1}<br/>
                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}<br/>
                  {order.shipping_address.country}
                </p>
              ) : <p className="text-xs text-[var(--ff-text-muted)] mt-1">No shipping address on file.</p>}
            </div>
            {canFulfill && (
              <div className="card-flat p-4">
                <p className="overline">Update fulfillment</p>
                <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Tracking URL (optional)" className="input-base mt-3" data-testid="tracking-input"/>
                <input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="Carrier (e.g. USPS, UPS)" className="input-base mt-2" data-testid="carrier-input"/>
                <div className="flex gap-2 flex-wrap mt-3">
                  {["packed", "shipped", "delivered"].map(s => (
                    <button key={s} disabled={busy} onClick={() => update(s)} className="btn-primary text-sm capitalize" data-testid={`mark-${s}`}>
                      Mark {s}
                    </button>
                  ))}
                  <button disabled={busy} onClick={() => update("cancelled")} className="btn-secondary text-sm text-rose-700" data-testid="mark-cancelled">
                    Cancel order
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ReportProblemModal
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        kind="vendor_escalation"
        orderId={orderId}
        vendorId={order?.items?.[0]?.vendor_id}
        topic="order"
        title="Escalate this order to the admin team"
        placeholder="What needs admin attention on this order? Refund authorization, address change, missed pickup, cold-chain failure — give as much context as you can."
        cta="Send escalation"
      />
    </div>
  );
}
